import type { BoardPlacement } from '../../../common/nodeSettings'
import { SCOREBOARD_BOARD_SIZE } from '../../../common/scoreboard'
import type { Rect } from '../controlGeometry'
import { BOARD_INSET } from './layouts'

/**
 * The geometry of the scoreboard over a picture: where the picture is inside
 * its view, where the board goes in it, and how big.
 *
 * Pure arithmetic, so the rules are tested on their own and the two views,
 * web and phone, draw from the same numbers. The board itself is drawn at its
 * natural size and scaled by one factor (`ScoreboardOverlay`), so everything
 * here is a rectangle whose width follows from its height and the board's
 * shape.
 *
 * Every size is set for the full board, batter and pitcher rows included
 * (`FULL_BOARD_HEIGHT` tall at its natural size): a board with nobody named
 * is drawn at the same scale and so comes out shorter, not stretched. A
 * board too big for its space is narrowed to fit, keeping its top-left
 * corner. However small that makes it, it is drawn: a board that shows and
 * hides with the size of the window would blink.
 */

export interface Size {
  width: number
  height: number
}

/**
 * Where a picture of `aspect` lands in a box, fitted whole and centred: the
 * box less any black bars above and below it, or either side.
 */
export function pictureRect(
  boxWidth: number,
  boxHeight: number,
  aspect: number
): Rect {
  if (!(aspect > 0) || boxWidth <= 0 || boxHeight <= 0) {
    return { x: 0, y: 0, w: Math.max(0, boxWidth), h: Math.max(0, boxHeight) }
  }
  let w = boxWidth
  let h = boxWidth / aspect
  if (h > boxHeight) {
    h = boxHeight
    w = boxHeight * aspect
  }
  return { x: (boxWidth - w) / 2, y: (boxHeight - h) / 2, w, h }
}

/** Whether two rectangles share any area; touching edges do not count. */
export function intersects(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  )
}

/** The board's width when it is drawn `height` tall. */
export function widthAt(natural: Size, height: number): number {
  return natural.height > 0 ? (height * natural.width) / natural.height : 0
}

/** The full board's natural height, its batter and pitcher rows included. */
export const FULL_BOARD_HEIGHT = SCOREBOARD_BOARD_SIZE.height

/**
 * The board drawn at `scale` from `x`, `y`, narrowed to fit inside `area`
 * from that corner; null where it would not be readable.
 */
function fitted(
  area: Rect,
  x: number,
  y: number,
  scale: number,
  natural: Size
): Rect | null {
  if (natural.width <= 0 || natural.height <= 0 || !(scale > 0)) return null
  let w = natural.width * scale
  let h = natural.height * scale
  const room = Math.min(
    1,
    Math.max(0, area.x + area.w - x) / w,
    Math.max(0, area.y + area.h - y) / h
  )
  w *= room
  h *= room
  if (!(h > 0)) return null
  return { x, y, w, h }
}

/**
 * The board over `area` by a node placement: its top-left corner at the
 * placement's fractions of the area, the full board `height` of the area
 * tall. It sits exactly there, whatever the player draws around it, and is
 * only ever narrowed to stay inside the area.
 */
export function placedBoardRect(
  area: Rect,
  placement: BoardPlacement,
  natural: Size
): Rect | null {
  return fitted(
    area,
    area.x + placement.x * area.w,
    area.y + placement.y * area.h,
    (placement.height * area.h) / FULL_BOARD_HEIGHT,
    natural
  )
}

/** The board over a single camera's picture. */
export function singleBoardRect(options: {
  /** The picture inside the view: the view less any black bars. */
  picture: Rect
  placement: BoardPlacement
  /** The board's own size, as measured. */
  natural: Size
}): Rect | null {
  return placedBoardRect(options.picture, options.placement, options.natural)
}

/**
 * The board over the landscape grid's top-left angle, four angles, in that
 * angle's own coordinates.
 */
export function tileBoardRect(
  tile: Rect,
  placement: BoardPlacement,
  natural: Size
): Rect | null {
  return placedBoardRect(
    { x: 0, y: 0, w: tile.w, h: tile.h },
    placement,
    natural
  )
}

/** Fractions of a point off an edge are arithmetic, not a crossing. */
const EDGE_SLACK = 1e-6

/**
 * The landscape grid of two angles: the board centred in the space the grid
 * leaves above them, as tall as that space allows less the insets.
 */
export function aboveBoardRect(
  screen: Size,
  /** The top of the angles. */
  top: number,
  natural: Size
): Rect | null {
  const scale = Math.max(0, top - 2 * BOARD_INSET) / FULL_BOARD_HEIGHT
  const across = Math.max(0, screen.width - 2 * BOARD_INSET)
  const narrowed = Math.min(
    scale,
    natural.width > 0 ? across / natural.width : 0
  )
  const w = natural.width * narrowed
  const h = natural.height * narrowed
  if (!(h > 0)) return null
  return { x: (screen.width - w) / 2, y: (top - h) / 2, w, h }
}

/**
 * The board moved out from under the hardware: the grid's pictures run to the
 * screen's edges, under a notch, the Dynamic Island and the rounded corners,
 * and the board must not. Where it crosses an edge of the `safe` area it goes
 * back inside by the board's inset; where it is clear it stays exactly where
 * it was put. A board the move would take past the far edge of the safe area,
 * or of the `area` it was placed in, is narrowed to fit from its new corner,
 * keeping its shape, as `fitted` narrows one: it never overflows.
 */
export function keptClear(rect: Rect, safe: Rect, area?: Rect): Rect {
  const move = (
    at: number,
    size: number,
    from: number,
    room: number
  ): number =>
    at < from - EDGE_SLACK
      ? from + BOARD_INSET
      : at + size > from + room + EDGE_SLACK
        ? Math.max(from + BOARD_INSET, from + room - BOARD_INSET - size)
        : at
  const x = move(rect.x, rect.w, safe.x, safe.w)
  const y = move(rect.y, rect.h, safe.y, safe.h)
  const right = Math.min(
    safe.x + safe.w,
    area == null ? Number.POSITIVE_INFINITY : area.x + area.w
  )
  const bottom = Math.min(
    safe.y + safe.h,
    area == null ? Number.POSITIVE_INFINITY : area.y + area.h
  )
  const room =
    rect.w > 0 && rect.h > 0
      ? Math.min(
          1,
          Math.max(0, right - x + EDGE_SLACK) / rect.w,
          Math.max(0, bottom - y + EDGE_SLACK) / rect.h
        )
      : 1
  return { x, y, w: rect.w * room, h: rect.h * room }
}
